import {
  Visibility,
  VisibilityOff,
  Lock as LockIcon
} from '@mui/icons-material';
import {
  TextField,
  InputAdornment,
  IconButton,
  Box
} from '@mui/material';
import React, { useState } from 'react';

import PasswordStrengthIndicator from './PasswordStrengthIndicator';

interface PasswordFieldProps {
  label: string;
  value?: string;
  onChange?: any; // Flexible for react-hook-form compatibility
  error?: boolean;
  helperText?: string;
  showStrengthIndicator?: boolean;
  autoComplete?: string;
  disabled?: boolean;
  required?: boolean;
  inputProps?: any;
  'data-testid'?: string;
  // For react-hook-form compatibility
  name?: string;
  ref?: any;
  onBlur?: any;
  // Accessibility
  ariaDescribedBy?: string;
  // Allow any additional props (for react-hook-form spread)
  [key: string]: any;
}

const PasswordField: React.FC<PasswordFieldProps> = ({
  label,
  value,
  onChange,
  error = false,
  helperText,
  showStrengthIndicator = true,
  autoComplete = "new-password",
  disabled = false,
  required = false,
  inputProps = {},
  'data-testid': testId,
  name,
  ref,
  onBlur,
  ariaDescribedBy
}) => {
  const [showPassword, setShowPassword] = useState(false);

  // Generate unique IDs for accessibility
  const fieldId = React.useId();
  const helperId = React.useId();
  const strengthId = React.useId();

  // Use provided value or empty string for controlled component
  const currentValue = value || '';

  // Build aria-describedby string
  const describedBy = [
    helperText ? helperId : undefined,
    showStrengthIndicator && currentValue ? strengthId : undefined,
    ariaDescribedBy
  ].filter(Boolean).join(' ') || undefined;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <Box>
      <TextField
        fullWidth
        id={fieldId}
        label={label}
        type={showPassword ? 'text' : 'password'}
        value={currentValue}
        onChange={handleChange}
        onBlur={onBlur}
        error={error}
        helperText={helperText}
        disabled={disabled}
        required={required}
        autoComplete={autoComplete}
        name={name}
        inputRef={ref}
        aria-describedby={describedBy}
        FormHelperTextProps={{
          id: helperId
        }}
        inputProps={{
          ...inputProps,
          'data-testid': testId,
          'aria-invalid': error
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockIcon />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={`toggle password visibility ${showPassword ? 'hide' : 'show'} password`}
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
                disabled={disabled}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {showStrengthIndicator && currentValue && (
        <PasswordStrengthIndicator
          password={currentValue}
          showRequirements={true}
          id={strengthId}
        />
      )}
    </Box>
  );
};

export default PasswordField;